import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import CardWithIcon from 'components/CardWithIcon/CardWithIcon';
import BankIcon from 'assets/icons/bank-with-stroke.svg?react';
import { TwButton } from 'components/TwButton/TwButton';
import usePSD2AccountSelection from './usePSD2AccountSelection';

export default function PSD2AccountSelection() {
  const { t } = useTranslation([
    'psd2_bank_account_select',
    'bank_id_account_selection',
  ]);
  const {
    accountList,
    selectedIBAN,
    isLoading,
    handleSelectAccount,
    handlePSD2IdentificationPayment,
    isPending,
  } = usePSD2AccountSelection();

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <CardWithIcon
        icon={<BankIcon data-test="psd2-icon" />}
        heading={t('page_title')}
        className="rounded-none lg:rounded-lg"
      >
        <p data-test="single-account-text">
          {accountList.length === 1
            ? t('bank_id_account_selection:info_text_single_account')
            : t('bank_id_account_selection:info_text_multiaccount')}
        </p>

        {accountList.length === 1 && (
          <div
            className="text-primary text-3xl font-semibold bg-background-accent p-5 text-center w-full"
            data-test="single-account"
          >
            {accountList[0].number}
          </div>
        )}

        {accountList.length > 1 && (
          <div className="w-full flex flex-col gap-2">
            {accountList.map(({ number, iban }, index) => (
              <button
                className={`items-center border rounded flex py-4 pl-4 bg-transparent transition-colors w-full hover:bg-background-accent ${
                  iban === selectedIBAN ? 'border-primary' : ''
                }`}
                onClick={() => handleSelectAccount(iban)}
                data-test={`account-${index}`}
                type="button"
                key={iban}
              >
                <div className="flex items-center gap-6">
                  <input
                    type="radio"
                    id={iban}
                    name={iban}
                    value={iban}
                    checked={iban === selectedIBAN}
                    className="appearance-none rounded-full h-4 w-4 border checked:ring-4 checked:ring-offset-2 checked:ring-primary checked:border-0"
                  />
                  <label
                    htmlFor={iban}
                    className="flex flex-col cursor-pointer"
                  >
                    <div
                      className="text-left font-light"
                      data-test={`account-${index}-label`}
                    >
                      {t('bank_id_account_selection:account_label')}
                    </div>
                    <div
                      className="font-bold"
                      data-test={`account-${index}-number`}
                    >
                      {number}
                    </div>
                  </label>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mx-auto lg:mx-0 w-full flex justify-center">
          <TwButton
            disabled={!selectedIBAN || isLoading || isPending}
            onClick={handlePSD2IdentificationPayment}
            data-test="confirm"
            pending={isPending}
          >
            {accountList.length === 1
              ? t('bank_id_account_selection:single_account_button')
              : t('bank_id_account_selection:info_text_multiaccount_button')}
          </TwButton>
        </div>
      </CardWithIcon>
    </>
  );
}
